import { MinusCircleIcon, PlusCircleIcon } from "@heroicons/react/24/solid";
import { Button } from "@moe/oss/ui/button";
import { FormLabel } from "@moe/oss/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@moe/oss/ui/select";
import { Textarea } from "@moe/oss/ui/textarea";
import { TokenCounter } from "@web/components/TokenCounter";
import { useAppContext } from "@web/route-services/root/AppContext";
import { MessageExample } from "@web/route-services/studio/schema";
import { AnimatePresence, motion } from "motion/react";

export function parseMessageExamples(messageExamplesStr: unknown): MessageExample[] {
  // Handle null, undefined, or empty cases
  if (!messageExamplesStr) return [];

  // If it's already an array of MessageExample, return it
  if (Array.isArray(messageExamplesStr)) {
    return messageExamplesStr.map((msg) => ({
      role: msg.role === "user" ? "user" : "character",
      content: String(msg.content || "")
    }));
  }

  // If it's a string that looks like JSON array, try to parse it
  if (typeof messageExamplesStr === "string") {
    const trimmed = messageExamplesStr.trim();
    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.map((msg) => ({
            role: msg.role === "user" ? "user" : "character",
            content: String(msg.content || "")
          }));
        }
      } catch (e) {
        // If JSON parsing fails, fall through to regular string parsing
      }
    }

    // Regular string parsing for legacy format
    return messageExamplesStr
      .split("\n\n")
      .filter((msg) => msg.trim())
      .map((msg) => {
        const splitIndex = msg.indexOf(":");
        if (splitIndex === -1) {
          return {
            role: "character" as const,
            content: msg.trim()
          };
        }

        const role = msg.slice(0, splitIndex).trim().toLowerCase();
        const content = msg.slice(splitIndex + 1).trim();

        return {
          role: (role === "user" ? "user" : "character") as "user" | "character",
          content
        };
      });
  }

  // If all else fails, return empty array
  return [];
}

export const MessageExamplesField = ({
  value = [],
  onChange,
  maxLength = 1000
}: {
  value: MessageExample[];
  onChange: (value: MessageExample[]) => void;
  maxLength?: number;
}) => {
  const { createDialog } = useAppContext();
  const handleAddMessage = () => {
    onChange([...value, { role: "character", content: "" }]);
  };

  // Modify the handleRemoveMessage function to show confirmation dialog:
  const handleRemoveMessage = (index: number) => {
    if (value[index].content.trim() === "") {
      const newMessages = value.filter((_, i) => i !== index);
      onChange(newMessages);
      return;
    }
    createDialog({
      title: "Confirm Deletion",
      description: "Are you sure you want to delete this message example?",
      actionLabel: "Delete",
      onAction: () => {
        const newMessages = value.filter((_, i) => i !== index);
        onChange(newMessages);
      }
    });
  };

  const handleMessageChange = (index: number, field: keyof MessageExample, newValue: string) => {
    const newMessages = [...value];
    newMessages[index] = {
      ...newMessages[index],
      [field]: field === "role" ? newValue : newValue
    };
    onChange(newMessages);
  };

  return (
    <div className="space-y-4">
      <div className="-mb-2.5 flex items-center justify-between">
        <span className="text-tx-secondary text-sm font-semibold md:text-lg ">Message Examples</span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleAddMessage}
          className="text-tx-secondary hover:text-tx-primary flex items-center gap-2 transition duration-200"
        >
          <PlusCircleIcon className="size-4" />
          Add Message
        </Button>
      </div>

      <div className="space-y-4">
        <AnimatePresence mode="sync">
          {value.map((message, index) => (
            <motion.div
              key={index}
              layout
              initial={{ opacity: 0, scale: 0.9, height: 0 }}
              animate={{ opacity: 1, scale: 1, height: "auto" }}
              exit={{
                opacity: 0,
                scale: 0.9,
                height: 0,
                transition: { duration: 0.2 }
              }}
              transition={{
                opacity: { duration: 0.2 },
                scale: { duration: 0.2 },
                height: { duration: 0.2 },
                layout: { duration: 0.2 }
              }}
              className="overflow-hidden"
            >
              <motion.div layout="position" className="border-line bg-card flex flex-col gap-2 rounded-lg border p-4">
                <motion.div layout="position" className="text-tx-primary flex items-center justify-between">
                  <Select value={message.role} onValueChange={(newRole) => handleMessageChange(index, "role", newRole)}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="character">Character</SelectItem>
                    </SelectContent>
                  </Select>

                  <FormLabel className="text-tx-secondary flex w-full items-center justify-between pr-2">
                    <span></span>
                    <TokenCounter content={message.content} />
                  </FormLabel>

                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveMessage(index)}
                      className="text-tx-secondary hover:text-tx-primary transition duration-200"
                    >
                      <MinusCircleIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </motion.div>

                <motion.div layout="position">
                  <Textarea
                    value={message.content}
                    onChange={(e) => handleMessageChange(index, "content", e.target.value)}
                    placeholder={`Enter ${message.role === "user" ? "user" : "character"} message example...`}
                    className="text-base font-medium"
                    maxLength={maxLength}
                  />
                </motion.div>
              </motion.div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};
